import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { T } from "@/data/content";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { Ticket, Loader2, Check, Mail } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// billet d'or 3D — suit le pointeur, flotte en continu
function GoldTicket({ code, qr, name }) {
    const ref = useRef(null);
    const [tilt, setTilt] = useState({ x: 0, y: 0 });

    const onMove = (e) => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        setTilt({ x: -py * 18, y: px * 22 });
    };

    return (
        <div style={{ perspective: "1200px" }} className="mx-auto w-full max-w-md">
            <motion.div
                ref={ref}
                data-testid="vip-ticket"
                onMouseMove={onMove}
                onMouseLeave={() => setTilt({ x: 0, y: 0 })}
                animate={{ rotateX: tilt.x, rotateY: tilt.y, y: [0, -10, 0] }}
                transition={{
                    rotateX: { type: "spring", stiffness: 120, damping: 14 },
                    rotateY: { type: "spring", stiffness: 120, damping: 14 },
                    y: { duration: 5, repeat: Infinity, ease: "easeInOut" },
                }}
                style={{ transformStyle: "preserve-3d" }}
                className="relative overflow-hidden rounded-2xl border border-gold/60 shadow-[0_30px_80px_rgba(212,175,55,0.25)]"
            >
                {/* or brossé */}
                <div className="absolute inset-0 bg-[linear-gradient(135deg,#3a2e08_0%,#d4af37_25%,#f5e6a8_40%,#d4af37_55%,#6b5310_75%,#d4af37_100%)]" />
                <div className="absolute inset-0 opacity-30 mix-blend-overlay"
                    style={{ background: "repeating-linear-gradient(115deg, transparent 0 6px, rgba(255,255,255,0.25) 6px 7px)" }} />
                <div className="relative p-7 text-ink" style={{ transform: "translateZ(40px)" }}>
                    <div className="flex items-center justify-between">
                        <p className="font-mono text-[10px] tracking-[0.4em] font-bold">GLOOM ENT.</p>
                        <Ticket size={20} />
                    </div>
                    <p className="mt-6 font-display text-4xl font-extrabold uppercase tracking-tight">VIP PASS</p>
                    <p className="mt-1 font-mono text-[10px] tracking-[0.3em]">EVENT PRIVÉ — ENTRÉE NOMINATIVE</p>
                    <div className="mt-6 flex items-end justify-between gap-4">
                        <div>
                            {name && <p className="font-mono text-[11px] tracking-[0.2em] font-bold">{name}</p>}
                            <p data-testid="vip-code" className="mt-1 font-mono text-sm tracking-[0.25em] font-bold">{code || "GLM-····-····"}</p>
                        </div>
                        {qr && (
                            <img
                                data-testid="vip-qr"
                                src={`data:image/png;base64,${qr}`}
                                alt="QR billet"
                                className="h-24 w-24 rounded-md border-2 border-ink/70 bg-white p-1"
                            />
                        )}
                    </div>
                    {/* perforation */}
                    <div className="absolute inset-y-0 left-5 border-l-2 border-dashed border-ink/25" />
                </div>
                {/* reflet animé */}
                <motion.div
                    className="pointer-events-none absolute inset-0"
                    animate={{ x: ["-120%", "120%"] }}
                    transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", repeatDelay: 2 }}
                    style={{ background: "linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.45) 50%, transparent 65%)", transform: "translateZ(60px)" }}
                />
            </motion.div>
        </div>
    );
}

export default function VipTicket() {
    const { lang } = useLang();
    const [form, setForm] = useState({ first_name: "", last_name: "", email: "" });
    const [busy, setBusy] = useState(false);
    const [ticket, setTicket] = useState(null);
    const [err, setErr] = useState("");
    const [remaining, setRemaining] = useState(null);

    useEffect(() => {
        fetch(`${API}/api/vip/count`)
            .then((r) => r.json())
            .then((d) => setRemaining(d.remaining))
            .catch(() => {});
    }, [ticket]);

    const claim = async () => {
        if (!form.first_name.trim() || !form.last_name.trim() || !form.email.includes("@")) {
            setErr(tx(lang, T.vip.err));
            return;
        }
        setBusy(true);
        setErr("");
        try {
            const r = await fetch(`${API}/api/vip/claim`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (!r.ok) throw new Error();
            setTicket(await r.json());
        } catch {
            setErr(tx(lang, T.vip.errServer));
        } finally {
            setBusy(false);
        }
    };

    const inputCls = "w-full border-b border-gold/30 bg-transparent py-2.5 font-mono text-[11px] tracking-[0.15em] text-cream outline-none placeholder:text-cream/30 focus:border-gold";

    return (
        <section data-testid="vip-page" className="relative min-h-screen bg-ink px-5 pb-24 pt-32 text-cream md:px-10">
            <div className="pointer-events-none absolute inset-0 opacity-25"
                style={{ background: "radial-gradient(55% 45% at 50% 0%, rgba(212,175,55,0.22) 0%, transparent 70%)" }} />
            <div className="relative mx-auto max-w-[900px]">
                <SectionLabel en={tx(lang, T.vip.label)} fr={tx(lang, T.vip.label)} />
                <FadeUp delay={0.1}>
                    <h1 className="mt-8 text-center font-display text-[clamp(2.2rem,7vw,5rem)] font-extrabold uppercase leading-none tracking-[-0.02em]">
                        {tx(lang, T.vip.title)}<span className="text-gold">.</span>
                    </h1>
                    <p className="mx-auto mt-4 max-w-md text-center font-mono text-[10px] leading-relaxed tracking-[0.25em] text-cream/50">
                        {tx(lang, T.vip.sub)}
                    </p>
                    {remaining !== null && (
                        <motion.div
                            data-testid="vip-remaining"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="mt-5 flex justify-center"
                        >
                            <span className="inline-flex items-center gap-2 border border-blood/60 bg-blood/10 px-5 py-2 font-mono text-[9px] tracking-[0.3em] text-blood">
                                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blood" />
                                {lang === "fr"
                                    ? remaining > 0 ? `PLUS QUE ${remaining} PLACE${remaining > 1 ? "S" : ""} — SUR 30` : "COMPLET"
                                    : remaining > 0 ? `ONLY ${remaining} LEFT — OF 30` : "SOLD OUT"}
                            </span>
                        </motion.div>
                    )}
                </FadeUp>

                <FadeUp delay={0.2} className="mt-14">
                    <GoldTicket
                        code={ticket?.code}
                        qr={ticket?.qr}
                        name={ticket ? `${form.first_name.toUpperCase()} ${form.last_name.toUpperCase()}` : null}
                    />
                </FadeUp>

                {!ticket ? (
                    <FadeUp delay={0.3} className="mx-auto mt-12 max-w-md">
                        <div className="space-y-5">
                            <div className="grid grid-cols-2 gap-4">
                                <input data-testid="vip-firstname" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder={tx(lang, T.vip.firstName)} className={inputCls} />
                                <input data-testid="vip-lastname" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder={tx(lang, T.vip.lastName)} className={inputCls} />
                            </div>
                            <input data-testid="vip-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={tx(lang, T.vip.email)} className={inputCls} />
                            {err && <p data-testid="vip-error" className="font-mono text-[10px] tracking-[0.2em] text-red-400">{err}</p>}
                            <button
                                data-testid="vip-submit"
                                onClick={claim}
                                disabled={busy}
                                className="flex w-full items-center justify-center gap-3 border border-gold bg-gold/10 px-6 py-4 font-mono text-[10px] tracking-[0.35em] text-gold transition-all hover:bg-gold hover:text-ink disabled:opacity-50"
                            >
                                {busy ? <Loader2 size={13} className="animate-spin" /> : <Ticket size={13} />}
                                {tx(lang, T.vip.claim)}
                            </button>
                            <p className="text-center font-mono text-[8px] tracking-[0.25em] text-cream/30">{tx(lang, T.vip.note)}</p>
                        </div>
                    </FadeUp>
                ) : (
                    <FadeUp delay={0.2} className="mx-auto mt-12 max-w-md text-center">
                        <p data-testid="vip-success" className="flex items-center justify-center gap-2 font-mono text-[11px] tracking-[0.25em] text-gold">
                            <Check size={14} /> {tx(lang, T.vip.success)}
                        </p>
                        <p className={`mt-3 flex items-center justify-center gap-2 font-mono text-[9px] tracking-[0.25em] ${ticket.email_sent ? "text-cream/60" : "text-cream/40"}`}>
                            <Mail size={12} /> {ticket.email_sent ? tx(lang, T.vip.emailSent) : tx(lang, T.vip.emailLater)}
                        </p>
                    </FadeUp>
                )}
            </div>
        </section>
    );
}
